import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useTheme } from "@mui/system";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import FileUpload from "./FileUpload";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../../redux/slices/snackbar";
import { Download } from "@mui/icons-material";
import { API_BASE_URL } from "../../../config";

const ITEM_HEIGHT = 62;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
    },
  },
};

const initialFormState = {
  nome: "",
  descrizione: "",
  fonte: {},
  link: "",
  idUidFile: "",
  tipoDocumento: {},
  tipoObbligatorio: {},
  statoAssicurativo: {},
  tipoCliente: {},
  bonificoObbligatorio: {},
  dataScadenzaMora: {},
  idStatoRichiesta: {},
  tipologie: {},
  autoGenerati: {},
};

// Select della modale quando si lavora su un accordo: nome in accordiOptions ->
// campo del documento in accordiGetDetails
const SELECT_DOCUMENTO = {
  tipoDocumento: "idTipo",
  fonte: "idFonte",
  autoGenerati: "idAutoGenerato",
  tipoObbligatorio: "idTipoObbligatorio",
  tipoCliente: "idTipoCliente",
  statoAssicurativo: "idStatoAssicurativo",
  idStatoRichiesta: "idStatoRichiesta",
  bonificoObbligatorio: "bonificoObbligatorio",
  dataScadenzaMora: "dataScadenzaMora",
};

// Alcuni dataset di accordiOptions hanno i value come stringhe ("1") mentre i
// documenti li hanno numerici: li uniformiamo a numeri per far funzionare le Select
const toNumero = (value) =>
  typeof value === "string" && value.trim() !== "" && !isNaN(value)
    ? Number(value)
    : value;

const normalizzaDataset = (dataset) =>
  (Array.isArray(dataset) ? dataset : []).map((el) => ({
    ...el,
    value: toNumero(el.value),
  }));

const DocumentoModale = ({
  open,
  setOpen,
  iduid,
  onRefresh,
  // Modalita' accordo: il documento arriva da accordiGetDetails, i dataset da
  // accordiOptions e "Applica" salva solo in locale (niente chiamate API)
  documentoLocale,
  opzioni,
  defaultOpzioni,
  idTipologia,
  onApplica,
}) => {
  const modalitaLocale = onApplica !== undefined;
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loadingPage, setLoadingPage] = useState(true);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [savedLink, setSavedLink] = useState("");
  const [savedIdUidFile, setSavedIdUidFile] = useState("");
  // Snackbar helper
  const handleSnackbar = (message, severity) => {
    dispatch(showSnackbar({ message, severity }));
  };

  // Reset and close modal
  const handleClose = () => {
    setForm(initialFormState);
    setFile(null);
    setErrors({});
    setOpen(false);
    setSavedLink("");
    setSavedIdUidFile("");
  };

  // Handle file upload (for fonte === 3)
  const handleFileUpload = (files) => {
    setFile(files[0]);
  };

  // Popola il form con i dati del documento (da documentiGetDetails o accordiGetDetails)
  const loadDocumento = (doc) => {
    setForm({
      iduid: doc.iduid,
      nome: doc.nome,
      descrizione: doc.descrizione,
      fonte: { dataset: doc.fonte[0].dataset, value: doc.fonte[0].value },
      link: doc.link || "",
      idUidFile: doc.idUidFile || "",
      tipoDocumento: { dataset: doc.tipo[0].dataset, value: doc.tipo[0].value },
      tipologie: {
        dataset: doc.tipologie[0].dataset,
        value: doc.tipologie[0].value,
      },
      tipoObbligatorio: {
        dataset: doc.tipoObbligatorio[0].dataset,
        value: doc.tipoObbligatorio[0].value,
      },
      statoAssicurativo: {
        dataset: doc.statoAssicurativo[0].dataset,
        value: doc.statoAssicurativo[0].value,
      },
      tipoCliente: {
        dataset: doc.tipoCliente[0].dataset,
        value: doc.tipoCliente[0].value,
      },
      bonificoObbligatorio: {
        dataset: doc.bonificoObbligatorio[0].dataset,
        value: doc.bonificoObbligatorio[0].value,
      },
      dataScadenzaMora: {
        dataset: doc.dataScadenzaMora[0].dataset,
        value: doc.dataScadenzaMora[0].value,
      },
      idStatoRichiesta: {
        dataset: doc.idStatoRichiesta[0].dataset,
        value: doc.idStatoRichiesta[0].value,
      },
      autoGenerati: {
        dataset: doc.autoGenerati[0].dataset,
        value: doc.autoGenerati[0].value,
      },
    });
    setSavedLink(doc.link || "");
    setSavedIdUidFile(doc.idUidFile || "");
    setLoadingPage(false);
  };

  // Modalita' accordo: documento esistente (riga di accordiGetDetails) oppure
  // nuovo (null), con i valori preselezionati di accordiOptions
  const loadDocumentoLocale = (doc) => {
    const select = Object.fromEntries(
      Object.entries(SELECT_DOCUMENTO).map(([nomeSelect, campo]) => {
        const value = toNumero(
          doc ? doc[campo] : defaultOpzioni?.[nomeSelect] ?? "",
        );
        return [
          nomeSelect,
          {
            dataset: normalizzaDataset(opzioni?.[nomeSelect]),
            value: value ?? "",
          },
        ];
      }),
    );
    // idAutoGenerato 0 = nessun documento autogenerato selezionato
    if (select.autoGenerati.value === 0) select.autoGenerati.value = "";

    setForm({
      ...initialFormState,
      ...select,
      nome: doc?.nome || "",
      descrizione: doc?.descrizione || "",
      link: doc?.link || "",
      idUidFile: doc?.idUidFile || "",
      tipologie: {
        dataset: normalizzaDataset(opzioni?.tipologia),
        value: toNumero(idTipologia) ?? "",
      },
    });
    setSavedLink(doc?.link || "");
    setSavedIdUidFile(doc?.idUidFile || "");
    setLoadingPage(false);
  };

  // Fetch document details
  const documentiGetDetails = async (iduid) => {
    const token = sessionStorage.getItem("token");
    try {
      const response = await fetch(`${API_BASE_URL}/api/documentiGetDetails`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + token,
        },
        body: JSON.stringify({
          token,
          data: { idUID: iduid },
        }),
      });
      const responseData = await response.json();
      if (responseData.status === -1) return navigate("/auth/sign-in");
      if (responseData.status === 0) return navigate("/private");

      loadDocumento(responseData.data[0]);
    } catch (error) {
      console.log(error);
      return error;
    }
  };

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "nome") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        nome: false,
      }));
    }

    if (name === "tipologia") {
      setForm((prev) => ({
        ...prev,
        tipologie: { ...prev.tipologie, value },
      }));
    } else if (name === "tipoObbligatorio") {
      setForm((prev) => ({
        ...prev,
        tipoObbligatorio: { ...prev.tipoObbligatorio, value },
      }));
    } else if (name === "statoAssicurativo") {
      setForm((prev) => ({
        ...prev,
        statoAssicurativo: { ...prev.statoAssicurativo, value },
      }));
    } else if (name === "tipoCliente") {
      setForm((prev) => ({
        ...prev,
        tipoCliente: { ...prev.tipoCliente, value },
      }));
    } else if (name === "idStatoRichiesta") {
      setForm((prev) => ({
        ...prev,
        idStatoRichiesta: { ...prev.idStatoRichiesta, value },
      }));
    } else if (name === "bonificoObbligatorio") {
      setForm((prev) => ({
        ...prev,
        bonificoObbligatorio: { ...prev.bonificoObbligatorio, value },
      }));
    } else if (name === "dataScadenzaMora") {
      setForm((prev) => ({
        ...prev,
        dataScadenzaMora: { ...prev.dataScadenzaMora, value },
      }));
    } else if (name === "tipoDocumento") {
      setFile(null);
      setForm((prev) => ({
        ...prev,
        tipoDocumento: { ...prev.tipoDocumento, value },
        fonte: { ...prev.fonte, value: value === 1 ? 0 : 1 },
        link: "",
        idUidFile: "",
        autoGenerati: { ...prev.autoGenerati, value: "" },
      }));
      setErrors((prev) => ({
        ...prev,
        idUidFile: false,
        link: false,
        autoGenerato: false,
      }));
    } else if (name === "fonte") {
      // Save current values before switching
      if (form.fonte.value === 2) setSavedLink(form.link);
      if (form.fonte.value === 3) setSavedIdUidFile(form.idUidFile);
      setFile(null);
      setForm((prev) => {
        let newForm = {
          ...prev,
          fonte: { ...prev.fonte, value },
        };
        // Restore previous value if switching back
        if (value === 2) {
          newForm.link = savedLink;
          newForm.idUidFile = savedIdUidFile;
          newForm.autoGenerati = { ...newForm.autoGenerati, value: "" };
        } else if (value === 3) {
          newForm.idUidFile = savedIdUidFile;
          newForm.link = "";
          newForm.autoGenerati = { ...newForm.autoGenerati, value: "" };
        } else if (value === 1) {
          newForm.idUidFile = savedIdUidFile;
          newForm.link = "";
        } else {
          newForm.link = "";
          newForm.idUidFile = "";
        }
        return newForm;
      });

      setErrors((prev) => ({
        ...prev,
        idUidFile: false,
        link: false,
        autoGenerato: false,
      }));
    } else if (name === "link") {
      setErrors((prev) => ({ ...prev, link: false }));
      setForm((prev) => ({
        ...prev,
        link: value,
      }));
      setSavedLink(value);
    } else if (name === "autoGenerato") {
      /* setErrors((prev) => ({ ...prev, link: false })); */
      setForm((prev) => ({
        ...prev,
        autoGenerati: { ...prev.autoGenerati, value },
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Save document
  const handleSave = async () => {
    // Error checks
    const newErrors = {
      nome: form.nome === "",
      file:
        savedIdUidFile === ""
          ? form.tipoDocumento.value !== 1 && form.fonte.value === 3 && !file
          : false,
      link:
        form.tipoDocumento.value !== 1 && form.fonte.value === 2 && !form.link,
      autoGenerato:
        form.tipoDocumento.value !== 1 &&
        form.fonte.value === 1 &&
        form.autoGenerati.value === "",
    };

    setErrors(newErrors);

    // If any error, show snackbar and stop
    if (Object.values(newErrors).some(Boolean)) {
      handleSnackbar("Compila tutti i campi obbligatori.", "error");
      return;
    }

    if (modalitaLocale) {
      handleApplica();
      return;
    }

    setLoadingPage(true);
    const payload = {
      id: null,
      nome: form.nome.trim(),
      descrizione: form.descrizione.trim(),
      fonte: form.fonte.value,
      tipoObbligatorio: form.tipoObbligatorio.value,
      tipoCliente: form.tipoCliente.value,
      statoAssicurativo: form.statoAssicurativo.value,
      idStatoRichiesta: form.idStatoRichiesta.value,
      bonificoObbligatorio: form.bonificoObbligatorio.value,
      dataScadenzaMora: form.dataScadenzaMora.value,
      tipo: form.tipoDocumento.value,
      tipologia: form.tipologie.value,
      link: form.link.trim(),
      idUidFile: form.idUidFile,
      idAutoGenerato:
        form.autoGenerati.value === "" ? 0 : form.autoGenerati.value,
    };

    const formData = new FormData();
    const token = sessionStorage.getItem("token");

    formData.append("data", JSON.stringify(payload));
    formData.append("file", "");

    // Only append file if fonte === 3
    if (form.tipoDocumento.value !== 1 && form.fonte.value === 3 && file) {
      formData.set("file", file);
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/documentiNewUpdate`, {
        method: "POST",
        headers: {
          authorization: "Bearer " + token,
        },
        body: formData,
      });

      const responseData = await response.json();

      if (responseData.status === -1) {
        navigate("/auth/sign-in");
      }
      if (responseData.status === 0) {
        handleSnackbar(responseData.message, "error");
        return;
      }
      if (responseData.status === 1) {
        handleSnackbar(responseData.message, "success");
      }
      setLoadingPage(false);
      handleClose();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.log(error);
      handleSnackbar("Errore durante il salvataggio.", "error");
      setLoadingPage(false);
      handleClose();
    }
  };

  // Modalita' accordo: aggiorna il documento in locale, verra' inviato con il
  // salvataggio globale dell'accordo (stessa forma delle righe di accordiGetDetails)
  const handleApplica = () => {
    // Il salvataggio globale e' in JSON: un nuovo file non puo' essere inviato
    if (form.tipoDocumento.value !== 1 && form.fonte.value === 3 && file) {
      handleSnackbar(
        "Il caricamento di un nuovo file non è ancora disponibile dall'accordo.",
        "error",
      );
      return;
    }

    const label = (nomeSelect) =>
      form[nomeSelect].dataset?.find(
        (el) => el.value === form[nomeSelect].value,
      )?.label ?? "";

    const richiesto = form.tipoDocumento.value === 1;
    const idFonte = richiesto ? 0 : form.fonte.value || 0;
    const idAutoGenerato =
      !richiesto && idFonte === 1 && form.autoGenerati.value !== ""
        ? form.autoGenerati.value
        : 0;
    const link = !richiesto && idFonte === 2 ? form.link.trim() : "";
    const idUidFile = !richiesto && idFonte === 3 ? form.idUidFile : "";

    const documento = {
      ...documentoLocale,
      // Documento nuovo: idUidDocumento vuoto, idLocale serve solo come chiave
      // della riga finche' l'accordo non viene salvato
      idUidDocumento: documentoLocale?.idUidDocumento ?? "",
      ...(documentoLocale
        ? {}
        : {
            idLocale: `nuovo-${Date.now()}`,
            isNew: 1,
            abilitato: 0,
            extFile: "",
            id: null,
          }),

      nome: form.nome.trim(),
      descrizione: form.descrizione.trim(),
      idTipo: form.tipoDocumento.value,
      tipo: label("tipoDocumento"),
      idFonte,
      fonte: idFonte ? label("fonte") : "",
      valoreFonte:
        idFonte === 1 ? label("autoGenerati") : idFonte === 2 ? link : "",
      link,
      idUidFile,
      idAutoGenerato,
      idTipoObbligatorio: form.tipoObbligatorio.value,
      tipoObbligatorio: label("tipoObbligatorio"),
      idTipoCliente: form.tipoCliente.value,
      idStatoAssicurativo: form.statoAssicurativo.value,
      idStatoRichiesta: form.idStatoRichiesta.value,
      bonificoObbligatorio: form.bonificoObbligatorio.value,
      dataScadenzaMora: form.dataScadenzaMora.value,
    };

    onApplica(documento);
    handleClose();
  };

  // Fetch details when modal opens
  useEffect(() => {
    if (!open) return;
    if (modalitaLocale) {
      loadDocumentoLocale(documentoLocale ?? null);
      return;
    }
    if (iduid !== null) {
      setLoadingPage(true);
      documentiGetDetails(iduid);
    }
  }, [open, iduid]);

  async function download(iduid) {
    setLoadingPage(true);
    const token = sessionStorage.getItem("token");
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/documentiDownload/${iduid}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: "Bearer " + token,
          },
          body: JSON.stringify({ token }),
        },
      );

      // Controlla se la risposta è JSON (errore) o un file (octet-stream)
      const contentType = res.headers.get("Content-Type");

      if (contentType && contentType.includes("application/json")) {
        // È un errore JSON, quindi leggi il JSON
        const response = await res.json();
        if (response.status === 0) {
          handleSnackbar(response.message, "error");
          setLoadingPage(false);
          return;
        } else {
          handleSnackbar("Errore sconosciuto", "error");
          setLoadingPage(false);
          return;
        }
      } else {
        // È un file da scaricare
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);

        // Prendi il nome file originale dall'header X-Filename, se esiste
        const originalFilename = res.headers.get("X-Filename") || iduid;

        const a = document.createElement("a");
        a.href = url;
        a.download = originalFilename; // usa il nome originale se disponibile
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        handleSnackbar("File scaricato con successo", "success");
        setLoadingPage(false);
        return;
      }
      return;
    } catch (error) {
      handleSnackbar("Download Fallito", "error");
    }
  }

  return (
    <Dialog
      open={open}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "600px",
            backgroundColor:
              theme.palette.mode === "dark" ? "#1B2635" : "#F7F9FC",
          },
        },
      }}
    >
      <DialogTitle>
        {iduid === "" ? "Aggiungi Documento" : "Modifica Documento"}
      </DialogTitle>
      <DialogContent>
        {loadingPage ? (
          <Box display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : (
          <Box width="100%" mt={2}>
            <Grid container spacing={3} mb={4}>
              <Grid item sm={4} xs={12}>
                <TextField
                  size="small"
                  margin="none"
                  label="Nome Documento"
                  value={form.nome}
                  onChange={handleChange}
                  error={errors.nome}
                  helperText={errors.nome ? "Il nome è obbligatorio" : ""}
                  fullWidth
                  id="nome-modale"
                  name="nome"
                  style={{ minWidth: 120 }}
                />
              </Grid>
              <Grid item sm={8} xs={12}>
                <TextField
                  size="small"
                  margin="none"
                  label="Descrizione Documento"
                  value={form.descrizione}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      descrizione: e.target.value,
                    }))
                  }
                  fullWidth
                  id="descrizione-modale"
                  name="descrizione"
                  style={{ minWidth: 120 }}
                />
              </Grid>
            </Grid>
            <Box border="0.5px solid #656565ff" padding={4}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl size="small" fullWidth margin="none">
                    <InputLabel id="tipo-obbligatorio-label">
                      Tipo Obbligatorio
                    </InputLabel>
                    <Select
                      labelId="tipo-obbligatorio-label"
                      id="tipo-obbligatorio"
                      name="tipoObbligatorio"
                      label="Tipo Obbligatorio"
                      value={form.tipoObbligatorio.value}
                      MenuProps={MenuProps}
                      onChange={handleChange}
                    >
                      {form.tipoObbligatorio.dataset?.map((el) => (
                        <MenuItem key={el.value} value={el.value}>
                          {el.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item sm={4} xs={12}>
                  <FormControl size="small" fullWidth margin="none">
                    <InputLabel id="tipo-cliente-label">
                      Tipo Cliente
                    </InputLabel>
                    <Select
                      labelId="tipo-cliente-label"
                      id="tipo-cliente"
                      name="tipoCliente"
                      label="Tipo Cliente"
                      value={form.tipoCliente.value}
                      MenuProps={MenuProps}
                      onChange={handleChange}
                    >
                      {form.tipoCliente.dataset?.map((el) => (
                        <MenuItem key={el.value} value={el.value}>
                          {el.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item sm={4} xs={12}>
                  <FormControl size="small" fullWidth margin="none">
                    <InputLabel id="stato-assicurativo-label">
                      Stato Assicurativo
                    </InputLabel>
                    <Select
                      labelId="stato-assicurativo-label"
                      id="stato-assicurativo"
                      name="statoAssicurativo"
                      label="Stato Assicurativo"
                      value={form.statoAssicurativo.value}
                      MenuProps={MenuProps}
                      onChange={handleChange}
                    >
                      {form.statoAssicurativo.dataset?.map((el) => (
                        <MenuItem key={el.value} value={el.value}>
                          {el.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item sm={4} xs={12}>
                  <FormControl size="small" fullWidth margin="none">
                    <InputLabel id="stato-richiesta-label">
                      Stato Richiesta
                    </InputLabel>
                    <Select
                      labelId="stato-richiesta-label"
                      id="stato-richiesta"
                      name="idStatoRichiesta"
                      label="Stato Richiesta"
                      value={form.idStatoRichiesta.value}
                      MenuProps={MenuProps}
                      onChange={handleChange}
                    >
                      {form.idStatoRichiesta.dataset?.map((el) => (
                        <MenuItem key={el.value} value={el.value}>
                          {el.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item sm={4} xs={12}>
                  <FormControl size="small" fullWidth margin="none">
                    <InputLabel id="bonifico-obbligatorio-label">
                      Bonifico Obbligatorio
                    </InputLabel>
                    <Select
                      labelId="bonifico-obbligatorio-label"
                      id="bonifico-obbligatorio"
                      name="bonificoObbligatorio"
                      label="Bonifico Obbligatorio"
                      value={
                        form.bonificoObbligatorio.value === 0
                          ? ""
                          : form.bonificoObbligatorio.value
                      }
                      MenuProps={MenuProps}
                      onChange={handleChange}
                    >
                      {form.bonificoObbligatorio.dataset?.map((el) => (
                        <MenuItem key={el.value} value={el.value}>
                          {el.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item sm={4} xs={12}>
                  <FormControl size="small" fullWidth margin="none">
                    <InputLabel id="mancata-circolazione-label">
                      Mancata Circolazione
                    </InputLabel>
                    <Select
                      labelId="mancata-circolazione-label"
                      id="mancata-circolazione"
                      name="dataScadenzaMora"
                      label="Mancata Circolazione"
                      value={
                        form.dataScadenzaMora.value === 0
                          ? ""
                          : form.dataScadenzaMora.value
                      }
                      MenuProps={MenuProps}
                      onChange={handleChange}
                    >
                      {form.dataScadenzaMora.dataset?.map((el) => (
                        <MenuItem key={el.value} value={el.value}>
                          {el.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
            <Box border="0.5px solid #656565ff" padding={4} mt={2}>
              <Grid container spacing={3} mb={4}>
                <Grid item xs={12}>
                  <FormControl size="small" fullWidth margin="none">
                    <InputLabel id="tipo-label">Tipo</InputLabel>
                    <Select
                      labelId="tipo-label"
                      id="tipo"
                      name="tipoDocumento"
                      label="Tipo"
                      MenuProps={MenuProps}
                      value={form.tipoDocumento.value}
                      onChange={handleChange}
                    >
                      {form.tipoDocumento.dataset?.map((el) => (
                        <MenuItem key={el.value} value={el.value}>
                          {el.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item sm={6} xs={12}>
                  {form.tipoDocumento.value !== 1 && (
                    <FormControl size="small" fullWidth margin="none">
                      <InputLabel id="fonte-label">Fonte</InputLabel>
                      <Select
                        labelId="fonte-label"
                        id="fonte"
                        name="fonte"
                        label="Fonte"
                        f
                        MenuProps={MenuProps}
                        value={form.fonte.value === 0 ? 1 : form.fonte.value}
                        onChange={handleChange}
                      >
                        {form.fonte.dataset?.map((el) => (
                          <MenuItem key={el.value} value={el.value}>
                            {el.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Grid>

                <Grid item sm={6} xs={12}>
                  {form.fonte.value === 3 && form.idUidFile !== "" && (
                    <Box display="flex" justifyContent="center" mb={4}>
                      <Button
                        variant="contained"
                        onClick={() => download(savedIdUidFile)}
                      >
                        Scarica File
                        <Download />
                      </Button>
                    </Box>
                  )}
                  {form.tipoDocumento.value !== 1 && form.fonte.value === 2 ? (
                    <TextField
                      size="small"
                      margin="none"
                      label="Link Contratto"
                      required
                      fullWidth
                      value={form.link}
                      onChange={handleChange}
                      id="link-modale"
                      name="link"
                      style={{ minWidth: 120 }}
                      error={errors.link}
                      helperText={errors.link ? "Il link è obbligatorio" : ""}
                    />
                  ) : form.tipoDocumento.value !== 1 &&
                    form.fonte.value === 3 ? (
                    !file ? (
                      <Box
                        display="flex"
                        flexDirection="column"
                        gap={4}
                        width="100%"
                      >
                        <FileUpload onFileAccepted={handleFileUpload} />
                        {errors.file && (
                          <Box sx={{ color: "red", fontSize: 12 }}>
                            Il file è obbligatorio
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          border: `2px dashed #ccc`,
                          padding: "20px",
                          textAlign: "center",
                          cursor: "pointer",
                          borderRadius: "4px",
                          color: errors.idUidFile ? "red" : "inherit",
                        }}
                      >
                        {file.name}
                      </Box>
                    )
                  ) : (
                    form.tipoDocumento.value !== 1 &&
                    form.fonte.value === 1 && (
                      <FormControl fullWidth size="small" margin="none">
                        <InputLabel id="autoGenerato-label">
                          Documenti Autogenerati
                        </InputLabel>
                        <Select
                          labelId="autoGenerato-label"
                          id="autoGenerato"
                          name="autoGenerato"
                          label="Documenti Autogenerati"
                          MenuProps={MenuProps}
                          value={form.autoGenerati.value}
                          onChange={handleChange}
                          error={errors.autoGenerato}
                        >
                          {form.autoGenerati.dataset?.map((el) => (
                            <MenuItem key={el.value} value={el.value}>
                              {el.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.autoGenerato && (
                          <FormHelperText sx={{ color: "red" }}>
                            Il documento autogenerato è obbligatorio
                          </FormHelperText>
                        )}
                      </FormControl>
                    )
                  )}
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          color="primary"
          sx={{ marginRight: "8px" }}
        >
          Chiudi
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{ marginRight: "8px" }}
          disabled={loadingPage}
        >
          Applica
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentoModale;
